import type { UserRole } from '@compass/client/types/membership';
import type { OrganizationUser } from '@compass/client/types/organization_user';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { RoleBadge } from '@/components/RoleBadge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProgramRoles } from '@/hooks/useProgramRoles';
import { useChangeProgramUserRole } from '@/hooks/useUserRoles';

interface ChangeProgramRoleDialogProps {
  organizationId: string;
  programId: string;
  user: OrganizationUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangeProgramRoleDialog: React.FC<ChangeProgramRoleDialogProps> = ({
  organizationId,
  programId,
  user,
  open,
  onOpenChange,
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: availableRolesData, isLoading: isLoadingRoles } = useProgramRoles();

  const changeRoleMutation = useChangeProgramUserRole();

  // Get current role directly from user object (passed from search API)
  const currentRole: UserRole | null = user?.roles && user.roles.length > 0 ? user.roles[0] : null;

  useEffect(() => {
    if (!open) {
      setSelectedRoleId('');
      setIsSubmitting(false);
    }
  }, [open]);

  const handleChangeRole = async () => {
    if (!user?.user_id || !selectedRoleId) return;

    setIsSubmitting(true);
    try {
      // Use atomic change role API - replaces existing role in a single operation
      await changeRoleMutation.mutateAsync({
        organization_id: organizationId,
        program_id: programId,
        user_id: user.user_id,
        new_role_id: selectedRoleId,
      });

      const newRoleName =
        availableRolesData?.roles?.find(r => r.id === selectedRoleId)?.name || 'new role';
      toast.success(`Changed ${user.name || user.email}'s role to ${newRoleName}`);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to change role');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  const availableRoles = availableRolesData?.roles || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Change Role - {user.name || user.email}</DialogTitle>
          <DialogDescription>
            Update the role assigned to this user in the program
          </DialogDescription>
        </DialogHeader>

        {isLoadingRoles ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current Role Display */}
            <div>
              <Label>Current Role</Label>
              <div className="mt-2 rounded-md border bg-muted/50 p-3">
                {currentRole ? (
                  <RoleBadge role={currentRole} size="sm" />
                ) : (
                  <span className="text-sm text-muted-foreground">No role assigned</span>
                )}
              </div>
            </div>

            {/* New Role Selection */}
            <div>
              <Label>New Role</Label>
              <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                <SelectTrigger className="mt-2 h-auto [&>span]:text-left">
                  <SelectValue placeholder="Select a new role..." />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles
                    .filter(role => role.id !== currentRole?.id)
                    .map(role => (
                      <SelectItem key={role.id} value={role.id || ''}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">{role.name}</span>
                          {role.description && (
                            <span className="text-xs text-muted-foreground">
                              {role.description}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleChangeRole}
            disabled={!selectedRoleId || isSubmitting || selectedRoleId === currentRole?.id}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Change Role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
